import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import {
  DASHBOARD_PATHS,
  resolveAllowedDashboardsForRole,
  type DashboardId,
} from '@tracxtms/shared';
import { useAuthStore } from '../../../stores/auth-store';
import { useDashboardPermissions } from '../../../hooks/use-dashboard-permissions';

export function DashboardRoute({
  dashboardId,
  children,
}: {
  dashboardId: DashboardId;
  children: ReactNode;
}) {
  const user = useAuthStore((state) => state.user);
  const role = user?.role;
  // Pull the org's effective grants so route guards honour Settings overrides.
  // While the query is loading we fall through to hardcoded defaults; the worst
  // case is a momentary redirect that re-evaluates once the matrix arrives.
  const { data: perms } = useDashboardPermissions();

  if (!user) {
    return null;
  }

  const grants = perms ? { dashboards: perms.matrix.dashboards, widgets: perms.matrix.widgets } : undefined;
  const allowed = resolveAllowedDashboardsForRole(role, grants);
  if (!allowed.includes(dashboardId)) {
    const fallback = allowed[0] ?? 'main';
    const redirectTo = DASHBOARD_PATHS[fallback];
    return redirectTo === DASHBOARD_PATHS[dashboardId] ? null : <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
